import type { IContributionTask } from '../types/WorkStream';
import type { IWorkStream } from '../types/WorkStream';
import { isLivingWageFundedWorkStream } from './workStreamUtils';

export type PendingWorkStreamTaskRequest = {
  groupId: string;
  groupName: string;
  message: string;
};

export type ContributionTaskDraft = IContributionTask & {
  pendingRequest?: PendingWorkStreamTaskRequest;
};

export function newContributionTaskId(): string {
  return crypto.randomUUID();
}

export function ensureContributionTaskId<T extends Pick<IContributionTask, 'id'>>(
  task: T
): T & { id: string } {
  const id = task.id?.trim();
  if (id) return { ...task, id };
  return { ...task, id: newContributionTaskId() };
}

export function toPersistedContributionTask(
  task: ContributionTaskDraft
): IContributionTask {
  const withId = ensureContributionTaskId(task);
  const persisted: IContributionTask = {
    id: withId.id,
    title: String(withId.title || '').trim(),
    status: withId.status || 'available',
  };
  if (withId.description && String(withId.description).trim()) {
    persisted.description = String(withId.description).trim();
  }
  if (withId.claimedBy) persisted.claimedBy = withId.claimedBy;
  if (withId.completedAt) persisted.completedAt = withId.completedAt;
  if (withId.allowMultiple !== undefined) persisted.allowMultiple = withId.allowMultiple;
  if (typeof withId.estimatedHours === 'number' && withId.estimatedHours > 0) {
    persisted.estimatedHours = withId.estimatedHours;
  }
  if (withId.suggestedTo) persisted.suggestedTo = withId.suggestedTo;
  if (withId.suggestedToName) persisted.suggestedToName = withId.suggestedToName;
  if (withId.suggestionRequestPostId) {
    persisted.suggestionRequestPostId = withId.suggestionRequestPostId;
  }
  if (withId.suggestionRequestGroupId) {
    persisted.suggestionRequestGroupId = withId.suggestionRequestGroupId;
  }
  return persisted;
}

export function applySuggestionToTask(
  task: ContributionTaskDraft,
  person: { userKey: string; userName: string } | null
): ContributionTaskDraft {
  if (!person) {
    const {
      suggestedTo: _suggestedTo,
      suggestedToName: _suggestedToName,
      suggestionRequestPostId: _suggestionRequestPostId,
      suggestionRequestGroupId: _suggestionRequestGroupId,
      pendingRequest: _pendingRequest,
      ...rest
    } = task;
    return rest;
  }
  const samePerson = task.suggestedTo === person.userKey;
  return {
    ...task,
    suggestedTo: person.userKey,
    suggestedToName: person.userName,
    ...(samePerson
      ? {}
      : {
          suggestionRequestPostId: undefined,
          suggestionRequestGroupId: undefined,
          pendingRequest: undefined,
        }),
  };
}

export function withPendingTaskRequest(
  task: ContributionTaskDraft,
  pending: PendingWorkStreamTaskRequest
): ContributionTaskDraft {
  return { ...ensureContributionTaskId(task), pendingRequest: pending };
}

export function withSentTaskRequest(
  task: ContributionTaskDraft,
  postId: string,
  groupId: string
): ContributionTaskDraft {
  const { pendingRequest: _pendingRequest, ...rest } = ensureContributionTaskId(task);
  return {
    ...rest,
    suggestionRequestPostId: postId,
    suggestionRequestGroupId: groupId,
  };
}

export function defaultWorkStreamTaskRequestBody(
  taskTitle: string,
  workStreamTitle: string
): string {
  const task = taskTitle.trim() || 'this task';
  const stream = workStreamTitle.trim() || 'this work stream';
  return `Could you take on “${task}” on the work stream “${stream}”?`;
}

export function buildWorkStreamTaskRequestCopy(params: {
  taskTitle: string;
  message: string;
  titleMaxLength?: number;
}): { title: string; body: string } {
  const titleMaxLength = params.titleMaxLength ?? 120;
  const title = params.taskTitle.trim().slice(0, titleMaxLength);
  const body = params.message.trim();
  return { title, body };
}

export function shouldClaimTaskOnRequestAccept(
  workStream: Pick<IWorkStream, 'fundingType'>,
  task: IContributionTask | undefined,
  userKey: string
): boolean {
  if (!task || !userKey) return false;
  if (isLivingWageFundedWorkStream(workStream)) return false;
  if (task.status === 'completed') return false;
  if (task.status === 'claimed' && task.claimedBy && task.claimedBy !== userKey && !task.allowMultiple) {
    return false;
  }
  return true;
}

export function claimContributionTaskInList(
  tasks: IContributionTask[],
  taskId: string,
  userKey: string
): IContributionTask[] {
  return tasks.map((task) => {
    if (task.id !== taskId) return task;
    return {
      ...task,
      status: 'claimed',
      claimedBy: userKey,
    };
  });
}
